import { normalisePath } from '~/utils/routes';

const FOCUSABLE_SELECTOR =
  'input, select, textarea, button, [tabindex]:not([tabindex="-1"])';

/**
 * Scroll to the highest .has-error element and focus its first focusable input.
 */
export const scrollToError = async (): Promise<void> => {
  await nextTick();

  const errors = [
    ...document.getElementsByClassName('has-error'),
  ] as HTMLElement[];
  if (errors.length === 0) return;

  // Compare viewport tops; offsetTop reports 0 for inputs in positioned containers
  const highest = errors.reduce((min, el) =>
    el.getBoundingClientRect().top < min.getBoundingClientRect().top ? el : min,
  );

  highest.scrollIntoView({ block: 'center', behavior: 'smooth' });

  // preventScroll so focus doesn't fight the scrollIntoView above
  const focusable = highest.matches(FOCUSABLE_SELECTOR)
    ? highest
    : highest.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  focusable?.focus({ preventScroll: true });
};

/**
 * Generate a store URL.
 *
 * @param subdomain
 * @param path
 */
export const storeRoute = (subdomain?: string, path?: string): string => {
  // Retrieve the partner's store root domain
  let url: string = (useCommonStore() as any).partner.store_root_domain;

  // If a subdomain is to be applied
  if (subdomain) {
    // Set the subdomain
    url = `${subdomain}.${url}`;
  }

  // If a path is to be applied
  if (path) {
    // Normalise the path
    path = normalisePath(path);

    // Set the path
    url = `${url}${path}`;
  }

  return url;
};

/**
 * Return the root domain of the current app.
 */
export const rootDomain = (): string => {
  // Split the hostname by "."
  const hostnameSegments: string[] = window.location.hostname.split('.');

  // Remove the first segment
  hostnameSegments.shift();

  // Join the remaining segments
  return hostnameSegments.join('.');
};

/**
 * Debounce a function.
 *
 * Note, we have no idea how this works, and trust its magic.
 *
 * Stolen from https://blog.logrocket.com/debounce-throttle-vue/.
 */
export function debounce(fn: Function, wait: number) {
  let timer: NodeJS.Timeout;

  return function (...args: any[]): void {
    if (timer) {
      clearTimeout(timer); // clear any pre-existing timer
    }

    // @ts-ignore
    const context = this; // get the current context

    timer = setTimeout((): void => {
      fn.apply(context, args); // call the function if time expires
    }, wait);
  };
}

/**
 * Copy text to the clipboard.
 */
export const copy = (text: string): void => {
  navigator.clipboard.writeText(text);
};

/**
 * Trigger a download of provided CSV data.
 */
export const downloadCsvFile = (data: string, filename: string): void => {
  // Construct a blob from the returned CSV data
  const blob: Blob = new Blob([data], { type: 'text/csv' });

  // Generate an object URL for the blob
  const url: string = URL.createObjectURL(blob);

  // Create an anchor link in the document
  const a: HTMLAnchorElement = document.createElement('a');

  // Use the blob's object URL as the anchor's destination
  a.setAttribute('href', url);

  // Set the anchor tag's download attribute with the desired filename
  a.setAttribute('download', filename);

  // Simulate clicking the anchor to download the file
  a.click();

  // Remove the anchor element
  a.remove();

  // Revoke the blob's object URL
  URL.revokeObjectURL(url);
};

/**
 * Trigger a download of provided PDF data.
 */
export const downloadPdfFile = (data: string, filename: string): void => {
  // Construct a blob from the returned PDF data
  const blob: Blob = new Blob([data], { type: 'application/pdf' });

  // Generate an object URL for the blob
  const url: string = URL.createObjectURL(blob);

  // Create an anchor link in the document
  const a: HTMLAnchorElement = document.createElement('a');

  // Use the blob's object URL as the anchor's destination
  a.setAttribute('href', url);

  // Set the anchor tag's download attribute with the desired filename
  a.setAttribute('download', filename);

  // Simulate clicking the anchor to download the file
  a.click();

  // Remove the anchor element
  a.remove();

  // Revoke the blob's object URL
  URL.revokeObjectURL(url);
};

/**
 * Trigger a download of a Blob, preserving its MIME type.
 */
export const downloadBlobFile = (blob: Blob, filename: string): void => {
  const url: string = URL.createObjectURL(blob);

  const a: HTMLAnchorElement = document.createElement('a');
  a.setAttribute('href', url);
  a.setAttribute('download', filename);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
};

/**
 * Validate validity.
 */
export const validateValidity = (validity: Validity): ValidityErrors => {
  const validityErrors: ValidityErrors = {};

  if (validity.type === 'period_from_purchase') {
    const periodFromPurchase: PeriodFromPurchase =
      validity as PeriodFromPurchase;

    let months: string | null = null;
    let days: string | null = null;

    if (periodFromPurchase.values.months === '') {
      months = 'Enter months';
    }

    if (periodFromPurchase.values.days === '') {
      days = 'Enter days';
    }

    if (
      Number(periodFromPurchase.values.months) === 0 &&
      Number(periodFromPurchase.values.days) === 0
    ) {
      months = 'Enter months';
      days = 'Enter days';
    }

    if (months || days) {
      validityErrors.values = {
        months: months as string,
        days: days as string,
      };
    }
  }

  if (validity.type === 'absolute_dates') {
    const absoluteDates: AbsoluteDates = validity as AbsoluteDates;

    let startDateError: string | null = null;
    let endDateError: string | null = null;

    if (absoluteDates.values.start_date === '') {
      startDateError = 'Enter a from date';
    }

    if (absoluteDates.values.end_date === '') {
      endDateError = 'Enter a to date';
    }

    const endDate: Date = new Date(absoluteDates.values.end_date);

    if (absoluteDates.values.end_date && endDate < new Date()) {
      endDateError = 'The to date must be in the future';
    }

    if (
      !(startDateError && endDateError) &&
      endDate < new Date(absoluteDates.values.start_date)
    ) {
      endDateError = 'The to date must be after the from date';
    }

    if (startDateError || endDateError) {
      validityErrors.values = {
        start_date: startDateError as string,
        end_date: endDateError as string,
      };
    }
  }

  if (validity.days) {
    if (validity.days?.length === 0) {
      validityErrors.days = 'At least one day is required';
    }
  }

  return validityErrors;
};

/**
 * Validate schedules.
 */
export const validateSchedules = (schedule: {
  activationDate?: string | null;
  deactivationDate?: string | null;
}): ScheduleErrors => {
  const scheduleErrors: ScheduleErrors = {};

  if (schedule.activationDate !== null) {
    let error: string | null = null;

    // If an activation date has been entered
    if (schedule.activationDate) {
      // If the activation date is not a valid date
      if (!isValidDate(new Date(schedule.activationDate))) {
        error = 'Enter a valid date';
      } else if (Date.parse(schedule.activationDate) < Date.now()) {
        // The activation date is before the current date
        error = 'Date cannot be in the past';
      }
    } else {
      error = 'Enter an activation date';
    }

    // If an error was found, set the activation error attribute
    if (error) {
      scheduleErrors.activationDate = error;
    }
  }

  if (schedule.deactivationDate !== null) {
    let error: string | null = null;

    // If a deactivation date has been entered
    if (schedule.deactivationDate) {
      // If the deactivation date is invalid
      if (!isValidDate(new Date(schedule.deactivationDate))) {
        error = 'Enter a valid date';
      } else if (
        schedule.activationDate &&
        !scheduleErrors.activationDate &&
        Date.parse(schedule.deactivationDate) <=
          Date.parse(schedule.activationDate)
      ) {
        // There is an activation date and the deactivation date is before activation
        error = 'Cannot be before activation';
      } else if (Date.parse(schedule.deactivationDate) < Date.now()) {
        // The deactivation date is before the current date
        error = 'Date cannot be in the past';
      }
    } else {
      error = 'Enter a deactivation date';
    }

    // If an error was found, set the deactivation error attribute
    if (error) {
      scheduleErrors.deactivationDate = error;
    }
  }

  return scheduleErrors;
};

/**
 * Validate tags have keys and values set.
 */
export const validateTags = (tags: KeyValuePairs): string => {
  // Check if hasDuplicateTags is set on the Pinia store set on the common Pinia store
  const duplicateTags: boolean =
    ((useCommonStore() as any).piniaStore as PiniaStoreWithDuplicateTags)
      .hasDuplicateTags ?? false;

  // If so, return error
  if (duplicateTags) {
    return 'Tag names must be unique';
  }

  // Convert the tags into a Javascript friendly format array of objects
  const tagsArray: ObjectFormattedTag[] = Object.entries(tags).map(
    ([key, value]): ObjectFormattedTag => ({ key, value }),
  );

  let error: string = '';

  // Check that each tag has a key and value set
  tagsArray.forEach((tag: ObjectFormattedTag): void => {
    if (tag.value === '') {
      error = 'Enter a tag value';
    }

    if (tag.key === '') {
      error = 'Enter a tag key';
    }
  });

  return error;
};

/**
 * Validate a date is a valid date.
 *
 * Checks if the given date is a Date object, and that it is not an "Invalid Date".
 */
export const isValidDate = (date: any): boolean =>
  // @ts-ignore since we are explicitly checking the date format against number
  date instanceof Date && !isNaN(date);

/**
 * Validate a model's scheduled activation and deactivation dates.
 *
 * If the model has a non-null activation date and/or deactivation date, ensure the field is not
 * empty and is a valid date.
 */
export const hasValidScheduledActivation = (model: ScheduledModel): boolean => {
  // If the model's activation date is not null
  if (model.activationDate !== null) {
    // Check that the model's activation date is not an empty string
    if (model.activationDate === '') {
      return false;
    }

    // Check that the model's activation date is a valid date
    if (!isValidDate(new Date(model.activationDate as string))) {
      return false;
    }
  }

  // If the model's deactivation date is not null
  if (model.deactivationDate !== null) {
    // Check that the model's deactivation date is not an empty string
    if (model.deactivationDate === '') {
      return false;
    }

    // Check that the model's deactivation date is a valid date
    if (!isValidDate(new Date(model.deactivationDate as string))) {
      return false;
    }
  }

  return true;
};

/**
 * Extract activationDate and deactivationDate from an array of schedules.
 *
 * Finds the first activation and deactivation schedules in the array and returns the scheduled_for
 * timestamp of each.
 */
export const extractScheduleTimestamps = (
  schedules: Schedule[],
): {
  activationDate: string | null;
  deactivationDate: string | null;
} => {
  let activationDate: string | null = null;
  let deactivationDate: string | null = null;

  // If the schedules array is not empty
  if (schedules.length) {
    // Find the first activation schedule
    const activationSchedule: Schedule | undefined = schedules.find(
      (schedule: Schedule) =>
        schedule.is_activation_change && schedule.attributes.is_active === true,
    );

    // Extract the schedule's scheduled_for timestamp
    activationDate = activationSchedule?.scheduled_for ?? null;

    // Find the first deactivation schedule
    const deactivationSchedule: Schedule | undefined = schedules.find(
      (schedule: Schedule) =>
        schedule.is_activation_change &&
        schedule.attributes.is_active === false,
    );

    // Extract the schedule's scheduled_for timestamp
    deactivationDate = deactivationSchedule?.scheduled_for ?? null;
  }

  return {
    activationDate,
    deactivationDate,
  };
};

/**
 * Convert to the local time equivalent string (for the input field)
 */
export const toDateTimeLocal = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Converts a UTC time string to the account's timezone.
 */
export const convertToAccountTimezone = (utcTime: string | number): string => {
  if (!utcTime) return '';

  // If we don't have an account or timezone, return the original value
  if (!useAccountStore().account || !useAccountStore().account?.timezone)
    return String(utcTime);

  try {
    // For time-only inputs, we need to create a full date object
    // We'll use today's date with the provided time
    const today = new Date();
    const timeStr = String(utcTime);

    // Parse the time string (expected format: HH:MM)
    const [hours, minutes] = timeStr.split(':').map(Number);

    // Create a date object with today's date and the provided time in UTC
    const date = new Date(
      Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate(),
        hours,
        minutes,
      ),
    );

    // Get the time parts in the account's timezone
    const options = {
      timeZone: useAccountStore().account?.timezone,
      hour: '2-digit' as const,
      minute: '2-digit' as const,
      hour12: false,
    };

    // Format the time in the account's timezone
    const formattedTime = date.toLocaleString('en-US', options);

    // Parse the formatted time to extract components
    // Format is HH:MM AM/PM or HH:MM (if hour12 is false)
    const timeParts = formattedTime.split(', ')[1] || formattedTime;
    const [hours24, minutes24] = timeParts.split(':');

    // Return the time in HH:MM format
    return `${hours24.padStart(2, '0')}:${minutes24.padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting to account timezone:', error);
    return String(utcTime);
  }
};

/**
 * Capitalise the first letter of the given string.
 */
export const ucFirst = (string: string): string =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const toSignificantFigures = (number: number, figures: number): string =>
  `0${number}`.slice(-figures);

/**
 * Download a file from a Response object.
 * This method handles extracting the file from the response and triggering the browser download.
 *
 * @param response The Response object containing the file data
 * @param filename Optional filename for the downloaded file (defaults to name from Content-Disposition)
 * @returns Promise that resolves when the download is complete
 */
export const downloadFileFromResponse = async (
  response: Response,
  filename?: string,
): Promise<void> => {
  try {
    // Get content disposition header to extract filename if not provided
    const contentDisposition = response.headers.get('content-disposition');
    let downloadFilename = filename;

    // Extract filename from Content-Disposition if available and no filename was provided
    if (!downloadFilename && contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (filenameMatch && filenameMatch[1]) {
        downloadFilename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Fallback filename if none was found
    if (!downloadFilename) {
      downloadFilename = 'download';
    }

    // Get the blob from the response
    const blob = await response.blob();

    // Determine content type from the response or use a generic one
    const contentType =
      response.headers.get('content-type') || 'application/octet-stream';

    // Create a URL for the blob
    const url = window.URL.createObjectURL(
      new Blob([blob], { type: contentType }),
    );

    // Create a temporary anchor element to trigger the download
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFilename;
    document.body.appendChild(a);
    a.click();

    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};
